import { Link, useParams } from 'react-router-dom';
import { Alert, Badge, Box, Heading, Spinner, Table } from '@hyphen/hyphen-components';

import { evidenceContentURL } from '../lib/api.js';
import { useResource } from '../lib/session.js';
import { ABSENT_LABEL, RunStatus, ShortHash, When, formatWhen } from './evidence-shared.jsx';

/** One collection run: what was asked, of whom, and what came back. */
export function EvidenceRun() {
  const { runID } = useParams();
  const { data: run, error } = useResource(`/evidence/runs/${encodeURIComponent(runID)}`);

  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!run) return <Spinner />;

  const issues = run.observations.filter((o) => o.level === 'issue');
  const notes = run.observations.filter((o) => o.level !== 'issue');

  return (
    <Box gap="4xl">
      <Link to="/admin/evidence">&larr; All evidence</Link>

      <Box gap="lg">
        <Box direction="row" alignItems="center" gap="xl">
          <Heading as="h1" size="lg">{run.source} run {run.id}</Heading>
          <RunStatus status={run.status} />
        </Box>
        <Box gap="xs" fontSize="sm" color="secondary">
          <span>Started {formatWhen(run.started_at)} by {run.triggered_by}</span>
          {run.identity && <span>Collected as {run.identity}</span>}
          <span>Collector version {run.tool_version}</span>
        </Box>
      </Box>

      {run.error && (
        <Alert variant={run.status === 'skipped' ? 'default' : 'warning'}
               title={run.status === 'skipped' ? 'Not collected' : 'Some captures failed'}>
          <Box as="pre" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{run.error}</Box>
        </Alert>
      )}

      {/*
        Issues before the table of calls. Someone opening a run is usually
        asking "is anything wrong?", and making them scroll past a list of
        successful captures to find out is the wrong order.
      */}
      {issues.length > 0 && (
        <Observations title="Potential issues" observations={issues} color="red" />
      )}
      {notes.length > 0 && (
        <Observations title="Worth knowing" observations={notes} color="grey" />
      )}
      {run.observations.length > 0 && (
        <Box fontSize="sm" color="secondary">
          These are observations, not verdicts. Whether a control is satisfied is a
          judgement for the control owner and the auditor.
        </Box>
      )}

      {run.artifact_list.length > 0 && (
        <Box gap="xl">
          <Heading as="h2" size="md">Captures</Heading>
          <Artifacts artifacts={run.artifact_list} />
        </Box>
      )}
    </Box>
  );
}

function Observations({ title, observations, color }) {
  return (
    <Box gap="lg">
      <Heading as="h2" size="md">{title}</Heading>
      <Box as="ul" gap="md" style={{ margin: 0, paddingLeft: '1.25rem' }}>
        {observations.map((o, index) => (
          <li key={`${o.artifact_id}-${index}`}>
            <Box direction="row" gap="sm" wrap alignItems="baseline">
              {o.controls.map((hai) => (
                <Badge key={hai} variant="soft" color={color}>{hai}</Badge>
              ))}
              <code>{o.artifact}</code>
            </Box>
            <Box as="p" style={{ margin: '0.25rem 0 0' }}>{o.message}</Box>
          </li>
        ))}
      </Box>
    </Box>
  );
}

export function Artifacts({ artifacts, showRun = false }) {
  const columns = [
    { heading: 'Capture', dataKey: 'name', render: (_cell, row) => <code>{row.name}</code> },
    {
      heading: 'Controls',
      dataKey: 'controls',
      render: (_cell, row) => (
        <Box direction="row" gap="xs" wrap>
          {row.controls.map((hai) => <Link key={hai} to={`/admin/controls/${hai}`}>{hai}</Link>)}
        </Box>
      ),
    },
    {
      heading: 'Result',
      dataKey: 'absent_reason',
      render: (_cell, row) => row.content_sha256 ? (
        <a href={evidenceContentURL(row.id)} target="_blank" rel="noreferrer">View response</a>
      ) : (
        <Box gap="xs">
          <Badge variant="outline">{ABSENT_LABEL[row.absent_reason] ?? row.absent_reason}</Badge>
          <Box as="span" fontSize="sm" color="secondary">{row.absent_detail}</Box>
        </Box>
      ),
    },
    { heading: 'SHA-256', dataKey: 'content_sha256', width: 140, render: (_cell, row) => <ShortHash value={row.content_sha256} /> },
    {
      heading: 'Collected',
      dataKey: 'collected_at',
      width: 190,
      render: (_cell, row) => showRun
        ? <Link to={`/admin/evidence/runs/${row.run_id}`}><When iso={row.collected_at} /></Link>
        : <When iso={row.collected_at} />,
    },
  ];
  return <Table rowKey="id" columns={columns} rows={artifacts} isScrollable={{ x: true }} />;
}
